import React, { useContext } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Chip, Typography } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"
import getIconComponent from "../utils/getIconComponent"
import { Assignment } from "@mui/icons-material"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

// Utilidad para mapear los tipos de encuesta
const getTipoEncuestaLabel = (tipo) => {
  switch (tipo) {
    case "RAP":
      return "Rápida"
    case "SAT":
      return "Satisfacción"
    case "PON":
      return "Ponderación"
    default:
      return tipo
  }
}

const EncuestasIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box
          sx={{
            textAlign: "center",
            mb: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#196C87",
          }}
        >
          <Assignment sx={{ fontSize: 32, mr: 1 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Administrador de Encuestas Dinámicas
          </Typography>
        </Box>

        <Box className={StyledRoot}>
          {/* GRILLA PRINCIPAL */}
          <CustomConditionalActionsTableServer
            endpoint="/AsigEncuestas/getEncuestasAsignables"
            errorMsgFilterSearch="Error al cargar la bandeja de encuestas"
            queryKeyModal="Encuestas_Bandeja_Principal"
            perPage={10}
            rowActionsWidthTable={100}
            rowActions={(row) => {
              // Validamos permisos para el botón EDITAR / GESTIONAR
              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "ASIGNAR" || action?.acccaption === "GESTIONAR",
              )

              const actions = []
              if (editarAction) {
                actions.push({
                  label: editarAction.acccaption,
                  key: "asignar",
                  icon: getIconComponent(editarAction.accnameicono, editarAction.acctipoico),
                  onClick: (row) => {
                    // Enviamos la fila seleccionada por el state del React Router (Estándar SIAC)
                    navigate("asignar", { state: { encuesta: row.original } })
                  },
                })
              }
              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              // Validamos permisos para CREAR y EXPORTAR en la barra superior
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )

              const toolbarActions = []

              if (crearAction) {
                toolbarActions.push({
                  label: crearAction.acccaption,
                  key: "crear",
                  icon: getIconComponent(crearAction.accnameicono, crearAction.acctipoico),
                  onClick: () => navigate("crear"),
                })
              }

              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction.acccaption,
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      onClick: ({ columns, data }) => {
                        const title = "Reporte de Encuestas"
                        device === "sm"
                          ? handleExportDataPdfSMScreen(columns, data, title, title)
                          : handleExportDataPdfLGScreen(columns, table.getCoreRowModel().rows, title, title)
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      onClick: ({ data }) => handleAllExportDataCSV(data, "Reporte_Encuestas_SIAC"),
                    },
                  ],
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "enccodigo",
                header: "Código",
                size: 130,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold", color: "#196C87" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "enctitulo",
                header: "Título de la Encuesta",
                size: 280,
                Cell: ({ cell }) => <span style={{ fontWeight: "500" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "enctipo",
                header: "Tipo",
                size: 120,
                Cell: ({ cell }) => <span>{getTipoEncuestaLabel(cell.getValue())}</span>,
              },
              {
                accessorKey: "encpublica",
                header: "Audiencia",
                size: 140,
                Cell: ({ cell }) => {
                  const esPublica = cell.getValue() === 1
                  return (
                    <Chip
                      label={esPublica ? "Pública (Global)" : "Lista Específica"}
                      color={esPublica ? "success" : "primary"}
                      size="small"
                      variant={esPublica ? "filled" : "outlined"}
                      sx={{ fontWeight: "bold" }}
                    />
                  )
                },
              },
              {
                accessorKey: "zoncodigo",
                header: "Zona",
                size: 100,
                Cell: ({ cell }) => {
                  const val = cell.getValue()
                  return val ? (
                    <span>{val}</span>
                  ) : (
                    <span style={{ fontStyle: "italic", color: "#9e9e9e" }}>Global</span>
                  )
                },
              },
              {
                accessorKey: "encstatus",
                header: "Estado",
                size: 120,
                Cell: ({ cell }) => {
                  const valor = cell.getValue()
                  return (
                    <Chip
                      label={valor === "A" ? "ACTIVO" : "INACTIVO"}
                      color={valor === "A" ? "success" : "default"}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  )
                },
              },
              {
                accessorKey: "encfecisys",
                header: "Fecha de Creación",
                size: 150,
                Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EncuestasIndex
